// Pieces shared by Monetisation → Revenue, Customers, Events and the player's Subscription tab
// (contracts/revenue.md §5): the range and the env/store switches (all in the URL), status and kind chips, the
// sandbox badge, money with its USD estimate, links to a player's subscription page, and the estimate note.

import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import type { Money } from "../../api/revenueSchemas";
import { tableStyles } from "../../components/table";
import { Button, Chip, Segmented, TextInput } from "../../components/ui";
import { addDays, daysBetween, todayUtc } from "../../lib/dates";
import { shortId } from "../../lib/format";
import {
  END_REASON_LABEL,
  ENV_LABEL,
  fmtMoney,
  fmtUsd,
  isEnvFilter,
  isStoreFilter,
  kindMeta,
  REVENUE_ENVS,
  REVENUE_STORES,
  regionName,
  statusMeta,
  STORE_FILTER_LABEL,
  STORE_LABEL,
  type RevenueEnvFilter,
  type RevenueStoreFilter,
} from "../../lib/revenue";
import pages from "../pages.module.css";
import r from "./revenue.module.css";

/** Changes some URL params (null removes one), keeping the rest. */
export function useParamSetter() {
  const [params, setParams] = useSearchParams();
  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  return [params, set] as const;
}

// ---------------------------------------------------------------- range (from, to) and scope (env, store)

const PRESETS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
] as const;

/** The range in the URL; absent means the last 30 days ending today (UTC), as the API defaults. */
export function useRange() {
  const [params, set] = useParamSetter();
  const today = todayUtc();
  const to = params.get("to") || today;
  const from = params.get("from") || addDays(to, -29);
  const span = daysBetween(from, to) + 1;
  const preset = to === today ? PRESETS.find((p) => Number(p.value) === span)?.value : undefined;
  const setRange = (f: string, t: string) => set(t === today && daysBetween(f, t) === 29 ? { from: null, to: null } : { from: f, to: t });
  return { from, to, today, preset, setRange };
}

export function RangeControls({ range }: { range: ReturnType<typeof useRange> }) {
  const { from, to, today, preset, setRange } = range;
  return (
    <>
      <Segmented
        label="Range"
        value={preset ?? "custom"}
        options={[...PRESETS, ...(preset ? [] : [{ value: "custom", label: "Custom" }])]}
        onChange={(v) => v !== "custom" && setRange(addDays(today, -(Number(v) - 1)), today)}
      />
      <TextInput type="date" value={from} max={to} onChange={(e) => e.target.value && setRange(e.target.value, to)} aria-label="From" />
      <TextInput type="date" value={to} min={from} max={today} onChange={(e) => e.target.value && setRange(from, e.target.value)} aria-label="To" />
    </>
  );
}

/** env (production by default) and store (all by default) in the URL. */
export function useScope() {
  const [params, set] = useParamSetter();
  const rawEnv = params.get("env");
  const rawStore = params.get("store");
  const env: RevenueEnvFilter = isEnvFilter(rawEnv) ? rawEnv : "production";
  const store: RevenueStoreFilter = isStoreFilter(rawStore) ? rawStore : "all";
  return {
    env,
    store,
    setEnv: (v: RevenueEnvFilter) => set({ env: v === "production" ? null : v }),
    setStore: (v: RevenueStoreFilter) => set({ store: v === "all" ? null : v }),
  };
}

/** The env and store as a query string for links between the revenue pages ("" for the defaults). */
export function scopeQuery(scope: { env: RevenueEnvFilter; store: RevenueStoreFilter }): string {
  const q = new URLSearchParams();
  if (scope.env !== "production") q.set("env", scope.env);
  if (scope.store !== "all") q.set("store", scope.store);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function ScopeControls({ scope }: { scope: ReturnType<typeof useScope> }) {
  return (
    <>
      <Segmented label="Environment" value={scope.env} options={REVENUE_ENVS.map((v) => ({ value: v, label: ENV_LABEL[v] }))} onChange={scope.setEnv} />
      <Segmented label="Store" value={scope.store} options={REVENUE_STORES.map((v) => ({ value: v, label: STORE_FILTER_LABEL[v] }))} onChange={scope.setStore} />
    </>
  );
}

/** "Reset" when anything is in the URL. */
export function ResetFilters() {
  const [params, setParams] = useSearchParams();
  if (!params.toString()) return null;
  return (
    <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>
      Reset
    </Button>
  );
}

// ---------------------------------------------------------------- chips and badges

export function SubStatusChip({ status, endReason }: { status: string; endReason?: string | null }) {
  const m = statusMeta(status);
  return (
    <span className={r.statusCell} data-status={status}>
      <Chip tone={m.tone} title={m.hint || undefined}>
        {m.label}
      </Chip>
      {status === "expired" && endReason ? <span className={r.sub}>{END_REASON_LABEL[endReason] ?? endReason.replace(/_/g, " ")}</span> : null}
    </span>
  );
}

export function KindChip({ kind, detail }: { kind: string; detail?: string | null }) {
  const m = kindMeta(kind);
  const label = kind === "cancelled" && detail === "during_trial" ? "cancelled in trial" : m.label;
  return (
    <Chip tone={m.tone} title={kind}>
      {label}
    </Chip>
  );
}

/** Sandbox (App Store Sandbox and Xcode, Stripe test mode) is marked wherever it shows; production isn't. */
export function SandboxBadge({ env }: { env: string | null | undefined }) {
  if (env !== "sandbox") return null;
  return (
    <span className={r.sandbox} title="Sandbox: TestFlight, App Review, sandbox accounts, Xcode, or Stripe test mode. Never counted with production.">
      sandbox
    </span>
  );
}

export function StoreCell({ store, env }: { store: string; env: string }) {
  return (
    <span className={r.storeCell}>
      <span>{STORE_LABEL[store] ?? store}</span>
      <SandboxBadge env={env} />
    </span>
  );
}

export function Storefront({ code }: { code: string | null | undefined }) {
  if (!code) return <span className={pages.faint}>—</span>;
  const name = regionName(code);
  return (
    <span className="mono" title={name ?? undefined}>
      {code}
    </span>
  );
}

// ---------------------------------------------------------------- money and players

/** The price in the customer's currency, with the USD estimate under it when the currency isn't USD. */
export function MoneyCell({ price, usd, align }: { price: Money | null; usd: number | null; align?: "right" }) {
  if (!price && usd === null) return <span className={pages.faint}>—</span>;
  const foreign = price && price.currency !== "USD" && usd !== null;
  return (
    <span className={r.money} data-align={align}>
      <span className="mono">{price ? fmtMoney(price) : fmtUsd(usd)}</span>
      {foreign ? <span className={r.sub}>≈ {fmtUsd(usd)}</span> : null}
    </span>
  );
}

/** A player, linking to their subscription page. */
export function PlayerCell({ userId, handle, country, children }: { userId: string | null; handle: string | null; country?: string | null; children?: ReactNode }) {
  if (!userId) return <span className={pages.faint}>no player yet</span>;
  return (
    <span className={r.player}>
      <Link to={`/players/${userId}/subscription`} className={tableStyles.id} title={userId} onClick={(e) => e.stopPropagation()}>
        {handle ? `@${handle}` : shortId(userId)}
      </Link>
      {country ? (
        <span className={r.sub} title={regionName(country) ?? undefined}>
          {country}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function EstimateNote({ children }: { children?: ReactNode }) {
  return (
    <p className={r.note} data-testid="estimate-note">
      Money here is an estimate: each charge counts its USD list price (config <code>paywall.prices</code>) unless it was paid in USD, and
      proceeds take off the store's cut (config <code>revenue.estimates</code>). Apple's Sales and Trends and Payments, and Stripe's dashboard,
      are the source of truth. {children}
    </p>
  );
}
